import { useRef, useState, useEffect } from "react";

import {
  Button,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";

//Unform e componentes
import { Form } from "@unform/web";
import UInput from "@/frontend/componentes/unform/UInput";
import UInputCurrency from "@/frontend/componentes/unform/UInputCurrency";

//Common
import {
  fn_calcularTotal,
  fn_formatarNumero,
  fn_parseValorMonetario,
} from "@/common/utils/util";

//Util
import { ICONES_BOTAO } from "@/frontend/utils/icones";
import { useEnterAsTab } from "@/frontend/utils/useEnterAsTab";
import { MENSAGENS_ERRO, showMensagemErro } from "@/frontend/utils/mensagens";

//API
import {
  api_listarAtivosEComValor,
  ROTA_LISTAR,
} from "@/frontend/services/api/api_list";

function TImpostos_cad({
  modalOpen,
  toggleModal,
  pagamentoEdit,
  valoresEdit,
  readOnly = false,
  reajuste = false,
  onConfirm,
}) {
  //### USE REF ###
  const botaoFecharRef = useRef(null);
  const formRef = useRef(null);
  const domRef = useRef(null); // ref do wrapper DOM
  useEnterAsTab(formRef, domRef);

  //### USE STATE ###
  const [impostos, setImpostos] = useState([]);

  const [valores, setValores] = useState({});
  const [valorTotal, setValorTotal] = useState(0);

  //### USE EFFECT ###
  //Inicialização da tela
  useEffect(() => {
    //Lista os impostos
    async function listarImpostos() {
      //Se for consulta, vai pegar os impostos gravados no pagamento senão vai listar os impostos ativos
      //e também os inativos que já possuem valor no pagamento, para o total fechar
      const result =
        (readOnly || reajuste) && pagamentoEdit
          ? pagamentoEdit.impostos
              .map((imp) => imp.imposto)
              .sort((a, b) => a.ordem - b.ordem)
          : await api_listarAtivosEComValor(
              ROTA_LISTAR.IMPOSTO,
              null,
              valoresEdit,
            );
      setImpostos(result);
    }

    listarImpostos();
    setValores(valoresEdit);
  }, []);

  //Ao carregar os impostos
  useEffect(() => {
    if (impostos.length > 0) {
      //Foco no primeiro campo habilitado da tela
      if (readOnly) {
        botaoFecharRef.current?.focus();
      } else {
        formRef?.current
          ?.getFieldRef(`valor_imposto[${impostos[0]?.id}]`)
          ?.current?.focus();
      }
    }
  }, [impostos]);

  //Ao alterar o valor de algum imposto
  useEffect(() => {
    //Calcula o total dos impostos
    setValorTotal(fn_calcularTotal(valores));
  }, [valores]);

  //### FUNÇÕES ###
  //Valida os dados do formulário
  function validarFormulario(body) {
    let valido = true;

    //Se o tipo valor for valor que subtrai do valor documento (Ex: Desconto)
    if (valorTotal >= Number(pagamentoEdit.valor_documento)) {
      showMensagemErro(
        MENSAGENS_ERRO.VALOR_CAMPO_X_DEVE_SER_MENOR_DO_QUE_VALOR_CAMPO_Y,
        "total",
        "documento",
      );
      valido = false;
    }
    return valido;
  }

  //### EVENTOS ###
  //OnBlur - Valor imposto
  function handleBlurImposto(event, idImposto) {
    const valor = Number(fn_parseValorMonetario(event.target.value) || 0);

    setValores((prev) => ({
      ...prev,
      [idImposto]: valor,
    }));
  }

  //OnClick - Botão Confirmar
  function handleBotaoCofirmar() {
    const body = formRef.current.getData();

    if (validarFormulario(body)) {
      let valores = {};
      impostos.map((imposto) => {
        valores[imposto.id] = Number(body.valor_imposto[imposto.id] || 0);
      });

      if (onConfirm) {
        const novoPagamento = {
          ...pagamentoEdit,
          valores_impostos: valores,
          valor_imposto: valorTotal,
        };

        onConfirm(novoPagamento);
        toggleModal();
      }
    }
  }

  return (
    <Modal isOpen={modalOpen} backdrop="static" size="lg">
      <div ref={domRef}>
        <Form ref={formRef}>
          <ModalHeader toggle={toggleModal} className="titulo-tela-modal">
            Impostos Retidos
          </ModalHeader>

          <ModalBody>
            <div className="m-1">
              {pagamentoEdit && pagamentoEdit.num_pag != 0 && (
                <Row>
                  <Col>
                    <Label className="fw-bold">
                      Pagamento: {pagamentoEdit.num_pag}
                    </Label>
                  </Col>
                </Row>
              )}

              <Table responsive>
                <thead>
                  <tr>
                    <td className="table-header">Imposto</td>
                    <td width="20%" align="right" className="table-header">
                      Valor (R$)
                    </td>
                  </tr>
                </thead>

                <tbody>
                  {impostos.map((imposto, index) => (
                    <tr key={index}>
                      <td>
                        {imposto.sigla} ({imposto.nome})
                        {imposto.situacao == "I" && (
                          <span className="text-danger texto-menor ms-2">
                            (inativo)
                          </span>
                        )}
                      </td>

                      <td align="right">
                        <div className="d-flex justify-content-end">
                          <UInputCurrency
                            name={`valor_imposto[${imposto.id}]`}
                            maxLength={12}
                            defaultValue={valores[imposto.id] || 0}
                            onBlur={(event) =>
                              handleBlurImposto(event, imposto.id)
                            }
                            onFocus={(event) => event.target.select()}
                            className="input-currency"
                            disabled={readOnly}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}

                  <tr className="tr-border-0">
                    <td align="right">
                      <div className="fw-bold  mt-2">TOTAL:</div>
                    </td>

                    <td align="right">
                      <div className="d-flex justify-content-end">
                        <UInput
                          key={"V" + valorTotal}
                          name="total"
                          value={fn_formatarNumero(valorTotal, 2)}
                          className="input-currency"
                          disabled={true}
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              name="btnConfirmar"
              color="primary"
              onClick={handleBotaoCofirmar}
              disabled={readOnly}
            >
              {ICONES_BOTAO.CONFIRMAR} Confirmar
            </Button>

            <Button
              innerRef={botaoFecharRef}
              name="btnFechar"
              color="secondary"
              onClick={toggleModal}
            >
              {ICONES_BOTAO.FECHAR} Fechar
            </Button>
          </ModalFooter>
        </Form>
      </div>
    </Modal>
  );
}

export default TImpostos_cad;
